import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Calendar, Users, TrendingUp } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Helmet } from 'react-helmet-async';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CustomerLifetimeValueCalculator = () => {
  const [averagePurchaseValue, setAveragePurchaseValue] = useState(100);
  const [purchaseFrequency, setPurchaseFrequency] = useState(4);
  const [customerLifespan, setCustomerLifespan] = useState(3);
  const [acquisitionCost, setAcquisitionCost] = useState(50);
  const [clv, setClv] = useState(0);
  const [roi, setRoi] = useState(0);

  useEffect(() => {
    calculateCLV();
  }, [averagePurchaseValue, purchaseFrequency, customerLifespan, acquisitionCost]);

  const calculateCLV = () => {
    const calculatedClv = averagePurchaseValue * purchaseFrequency * customerLifespan;
    setClv(parseFloat(calculatedClv.toFixed(2)));
    const calculatedRoi = ((calculatedClv - acquisitionCost) / acquisitionCost) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const chartData = {
    labels: ['Customer Lifetime Value', 'Customer Acquisition Cost'],
    datasets: [
      {
        label: 'Value in $',
        data: [clv, acquisitionCost],
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)'],
      },
    ],
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-lifetime-value-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Customer Lifetime Value (CLV) Calculator: Measure Customer Worth | Free Tool</title>
        <meta name="description" content="Calculate Customer Lifetime Value (CLV) instantly with our free calculator. Measure customer profitability, ROI, and get optimization strategies to increase customer value." />
        <meta name="keywords" content="CLV calculator, customer lifetime value calculator, customer value calculator, CLV ROI calculator, customer profitability calculator, marketing metrics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CLV Calculator: Measure Your Customer Lifetime Value" />
        <meta property="og:description" content="Calculate Customer Lifetime Value instantly. Get insights into customer profitability and ROI with our free CLV calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CLV Calculator: Measure Your Customer Lifetime Value" />
        <meta property="twitter:description" content="Calculate Customer Lifetime Value instantly. Get insights into customer profitability and ROI with our free CLV calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Customer Lifetime Value Calculator",
            "description": "Free calculator to measure and optimize customer lifetime value (CLV)",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Customer Lifetime Value",
              "Measure Customer ROI",
              "Compare CLV vs CAC",
              "Visual data representation",
              "Optimization recommendations"
            ],
            "about": {
              "@type": "Thing",
              "name": "Customer Lifetime Value",
              "description": "The total revenue a business can expect from a single customer account throughout the business relationship."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Business Owners, Marketing Professionals, Customer Success Managers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Customer Lifetime Value",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter average purchase value"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input purchase frequency per year"
                },
                {
                  "@type": "HowToStep",
                  "text": "Specify customer lifespan in years"
                },
                {
                  "@type": "HowToStep",
                  "text": "Add customer acquisition cost"
                },
                {
                  "@type": "HowToStep",
                  "text": "View calculated CLV and ROI with optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Customer Lifetime Value Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Average Purchase Value</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={averagePurchaseValue}
                  onChange={(e) => setAveragePurchaseValue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Purchase Frequency (per year)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={purchaseFrequency}
                  onChange={(e) => setPurchaseFrequency(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="4"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Customer Lifespan (years)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={customerLifespan}
                  onChange={(e) => setCustomerLifespan(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="3"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Customer Acquisition Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={acquisitionCost}
                  onChange={(e) => setAcquisitionCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Customer Lifetime Value</p>
              <p className="text-3xl font-bold text-indigo-600">${clv}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">ROI</p>
              <p className="text-3xl font-bold text-green-600 flex items-center">
                {roi}%
                <TrendingUp className="w-6 h-6 ml-2" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">CLV vs CAC Comparison</h2>
        <Bar data={chartData} />
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Best Practices</h2>
        <p className="text-gray-600">
          Your Customer Lifetime Value (CLV) is ${clv}, which means this is the total revenue you can expect from a single customer over the course of your relationship. Your Customer Acquisition Cost (CAC) is ${acquisitionCost}.
        </p>
        <p className="text-gray-600 mt-2">
          The ROI of {roi}% indicates that for every dollar spent on acquiring a customer, you're generating ${(roi / 100 + 1).toFixed(2)} in customer lifetime value.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 3-5x CAC</li>
          <li>SaaS: 3-4x CAC</li>
          <li>Retail: 2-3x CAC</li>
          <li>Financial Services: 4-6x CAC</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Improving Your CLV</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Increase average purchase value through upselling and cross-selling</li>
          <li>Improve customer retention to extend customer lifespan</li>
          <li>Implement loyalty programs to encourage repeat purchases</li>
          <li>Provide excellent customer service to build long-term relationships</li>
          <li>Personalize marketing efforts to increase purchase frequency</li>
          <li>Optimize pricing strategy based on customer segments</li>
          <li>Develop customer engagement programs</li>
          <li>Create targeted retention campaigns</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerLifetimeValueCalculator;
